<template>
    <span class="contents">
        <button
        ref="buttonElement"
        v-bind="buttonAttrs"
        :style="{ anchorName: tooltipAnchorName }"
        class="inline-flex items-center justify-center gap-2 rounded border transition cursor-pointer"
        :class="[
            getClasses,
            {'px-5 py-3 text-lg': big && !textOnly},
            {'px-3 py-2 text-lg': big && textOnly},
            {'py-2 px-4': !big && !small && !textOnly},
            {'py-1 px-2': !big && !small && textOnly},
            {'py-1 px-2 text-sm': small && !textOnly},
            {'py-0.5 px-1.5 text-sm': small && textOnly},
            {'cursor-default! pointer-events-none!': disabled}
        ]"
        :disabled="disabled"
        :aria-describedby="ariaDescribedby"
        :aria-keyshortcuts="tooltipShortcut"
        @pointerenter="showTooltip"
        @pointerleave="scheduleTooltipClose"
        @focusin="showTooltip"
        @focusout="scheduleTooltipClose"
        @keydown.esc="hideTooltip"
        @click="handle_click"
    >
        <span
            v-if="iconComponent"
            aria-hidden="true"
            class="inline-flex shrink-0 items-center self-stretch border-r-0 border-current/50"
            :class="{'pr-2': !textOnly, 'pr-0': textOnly}"
        >
            <component
                :is="iconComponent"
                class="button-icon"
                :class="big ? 'h-5 w-5' : small ? 'h-3.5 w-3.5' : 'h-4 w-4'"
            />
        </span>
        <slot/>
        </button>
        <span
            :id="tooltipId"
            ref="tooltipElement"
            popover="hint"
            role="tooltip"
            class="button-tooltip pointer-events-auto fixed z-100 w-max max-w-[calc(100vw-1rem)] rounded-md border-0 bg-text px-2 py-1 text-xs font-normal leading-4 text-background shadow-md sm:max-w-64"
            :style="{ positionAnchor: tooltipAnchorName }"
            @pointerenter="showTooltip"
            @pointerleave="scheduleTooltipClose"
        >
            <span class="flex items-center justify-between gap-2">
                <span v-if="tooltipText" class="min-w-0">{{ tooltipText }}</span>
                <kbd
                    v-if="tooltipShortcut"
                    class="shrink-0 whitespace-nowrap font-monos"
                    :class="{'border-l border-current/40 pl-2': tooltipText}"
                >Shortcut: {{ tooltipShortcut }}</kbd>
            </span>
        </span>
    </span>
</template>

<script lang="ts" setup>
import { availableIconNames, iconComponents } from '../icons/iconRegistry'

defineOptions({ inheritAttrs: false })

type ButtonTooltip = string | {
    key: string
    text?: string
}

type PopoverElement = HTMLElement & {
    showPopover(options?: { source?: HTMLElement }): void
}

const { confirm } = useConfirm()
const attrs = useAttrs()

const props = defineProps({
    type: {
        type: String as PropType<'primary' | 'secondary' | 'destructive' | 'basic'>,
        default: 'primary'
    },
    big: {
        type: Boolean,
        default: false
    },
    small: {
        type: Boolean,
        default: false
    },
    borderOnly: {
        type: Boolean,
        default: false
    },
    textOnly: {
        type: Boolean,
        default: false
    },
    icon: {
        type: String,
        default: null,
        validator: (value: string) => availableIconNames.includes(value)
    },
    disabled: Boolean,
    tooltip: {
        type: [String, Object] as PropType<ButtonTooltip>,
        default: undefined
    },
    confirm: {
        type: Object as PropType<{ title: string, body: string, action: Function }>,
        default: null
    }
})

const iconComponent = computed(() => props.icon ? iconComponents[props.icon] : null)
const tooltipShortcut = computed(() => typeof props.tooltip === 'object' ? props.tooltip.key : undefined)
const tooltipText = computed(() => typeof props.tooltip === 'object' ? props.tooltip.text : props.tooltip)
const tooltipId = useId()
const tooltipAnchorName = `--button-tooltip-${ tooltipId.replaceAll(':', '') }`
const ariaDescribedby = computed(() => {
    const existingDescription = attrs['aria-describedby']
    return [
        typeof existingDescription === 'string' ? existingDescription : undefined,
        props.tooltip ? tooltipId : undefined,
    ].filter(Boolean).join(' ') || undefined
})
const buttonAttrs = computed(() => {
    const { 'aria-describedby': _ariaDescribedby, ...rest } = attrs
    return rest
})

const buttonElement = ref<HTMLButtonElement>()
const tooltipElement = ref<PopoverElement>()
let openTooltipTimer: ReturnType<typeof setTimeout> | undefined
let closeTooltipTimer: ReturnType<typeof setTimeout> | undefined

const clearOpenTooltipTimer = () => {
    if (openTooltipTimer) clearTimeout(openTooltipTimer)
    openTooltipTimer = undefined
}

const clearTooltipTimer = () => {
    if (closeTooltipTimer) clearTimeout(closeTooltipTimer)
    closeTooltipTimer = undefined
}

const showTooltip = () => {
    clearTooltipTimer()
    if (
        !props.tooltip
        || !buttonElement.value
        || !tooltipElement.value
        || openTooltipTimer
        || tooltipElement.value.matches(':popover-open')
    ) return

    openTooltipTimer = setTimeout(() => {
        openTooltipTimer = undefined
        if (
            props.tooltip
            && buttonElement.value
            && tooltipElement.value
            && buttonElement.value.matches(':hover, :focus-within')
        ) tooltipElement.value.showPopover({ source: buttonElement.value })
    }, 300)
}

const hideTooltip = () => {
    clearOpenTooltipTimer()
    if (tooltipElement.value?.matches(':popover-open')) tooltipElement.value.hidePopover()
}

const scheduleTooltipClose = () => {
    clearOpenTooltipTimer()
    clearTooltipTimer()
    closeTooltipTimer = setTimeout(() => {
        if (
            buttonElement.value?.matches(':hover, :focus-within')
            || tooltipElement.value?.matches(':hover')
        ) return
        hideTooltip()
    }, 100)
}

watch(() => props.tooltip, (tooltip) => {
    if (!tooltip) hideTooltip()
})

onBeforeUnmount(() => {
    clearOpenTooltipTimer()
    clearTooltipTimer()
})

const handle_click = async (_evt: Event) => {
    if (props.confirm) {
        _evt.preventDefault()
        await open_dialog()
    }
}

const open_dialog = async () => {
    const ok = await confirm({
        title: props.confirm.title,
        body: props.confirm.body,
        type: props.type === 'destructive' ? 'destructive' : 'normal'
    })
    if (ok) props.confirm.action()
}

const getClasses = computed(() => {
    const borderColors = (() => {
        if (props.disabled) return '!bg-background-accent !border-background-accent !text-text-light'
        if (props.textOnly) return 'border-transparent'
        if (props.type === 'destructive') return `${ props.borderOnly ? 'border-destructive' : 'border-transparent' } hover:border-destructive`
        else if (props.type === 'secondary') return `${ props.borderOnly ? 'border-secondary' : 'border-transparent' } hover:border-secondary`
        else if (props.type === 'primary') return `${ props.borderOnly ? 'border-primary' : 'border-transparent' } hover:border-primary`
        else if (props.type === 'basic') return `${ props.borderOnly ? 'border-inactive' : 'border-transparent' } hover:border-inactive`
    })()
    const textColors = (() => {
        if (props.textOnly) {
            if (props.type === 'primary') return 'text-primary fill-primary'
            if (props.type === 'secondary') return 'text-secondary fill-secondary'
            if (props.type === 'destructive') return 'text-destructive fill-destructive'
            if (props.type === 'basic') return 'text-text fill-text'
        }
        if (!props.borderOnly) {
            const base = 'text-background fill-background'
            if (props.type === 'primary') return base
            if (props.type === 'secondary') return `${ base } hover:text-secondary`
            if (props.type === 'destructive') return base
            if (props.type === 'basic') return `${ base } text-text `
        } else {
            if (props.type === 'primary') return 'text-primary fill-primary hover:text-background'
            else if (props.type === 'secondary') return 'text-secondary'
            else if (props.type === 'destructive') return 'text-destructive hover:text-background-accent'
        }
    })()
    const bgColors = (() => {
        if (props.textOnly) {
            if (props.type === 'primary') return 'bg-transparent hover:bg-primary/10'
            if (props.type === 'secondary') return 'bg-transparent hover:bg-secondary/10'
            if (props.type === 'destructive') return 'bg-transparent hover:bg-destructive/10'
            if (props.type === 'basic') return 'bg-transparent hover:bg-inactive/20'
        }
        if (!props.borderOnly) {
            if (props.type === 'primary') return 'bg-primary hover:bg-primary/80'
            if (props.type === 'basic') return 'bg-inactive hover:bg-background-accent-dark'
            else if (props.type === 'secondary') return 'bg-secondary hover:bg-secondary-dark'
            else if (props.type === 'destructive') return 'bg-destructive hover:bg-destructive-dark'
        } else {
            const base = 'bg-transparent'
            if (props.type === 'primary') return `${ base } hover:bg-primary`
            else if (props.type === 'secondary') return `${ base } hover:bg-secondary-light`
            else if (props.type === 'destructive') return `${ base } hover:bg-destructive`
            // return `bg-transparent! bg-opacity-30 hover:!bg-${props.type}-light`
        }
    })()

    const scale = 'hover:scale-y-105 hover:scale-x-102'
    return ` ${ borderColors } ${ textColors } ${ bgColors } ${ scale }`
})
</script>

<style scoped lang="scss">
.button-icon {
    fill: currentColor;

    &[fill='none'] {
        fill: none;
    }
}

.button-tooltip {
    inset: auto;
    margin: 0 0 0.5rem;
    position-area: top center;
    position-try-fallbacks: flip-block;
}

.primary {
    @apply bg-primary;
    @apply text-background;
    @apply hover:bg-primary-light;
}

.secondary {
    @apply border;
    @apply text-text;
    @apply hover:bg-background-accent-light;
}

.secondary-destructive {
    @apply border-rose-600;
    @apply text-rose-600;
    // @apply hover:bg-destructive-light;
    // @apply hover:bg-rose-900;
}

.secondary-primary {
    @apply border-primary;
    @apply text-primary;
}
</style>
